import Report from '../models/Report';
import Parse from 'parse';
import AgencyApplication from '../models/AgencyApplication';
import Agent from '../models/Agent';
import User from '../models/User';

export const agencyService = {
  /**
   * Check if user has a pending agency application
   * @param {Parse.User} user - Current user
   * @returns {Promise<AgencyApplication|null>} The pending application if exists, null otherwise
   */
  async checkPendingApplication(user) {
    if (!user) return null;

    const query = new Parse.Query(AgencyApplication);
    query.equalTo(AgencyApplication.keys.AUTHOR_ID, user.id);
    query.containedIn(AgencyApplication.keys.STATUS, [AgencyApplication.status.REFUSED, AgencyApplication.status.PENDING]);
    query.include(AgencyApplication.keys.REPORT);
    
    return query.first();
  },
  /**
   * Submit agency registration form by creating a report
   * @param {Object} params
   * @param {Parse.User} params.user - Current user submitting the form
   * @param {string} params.agencyName - Name of the agency
   * @param {string} params.agencyDescription - Description of the agency
   * @param {string} params.contactEmail - Contact email for the agency
   * @param {string} params.paymentMethod - Payment method for the agency
   * @param {Parse.File[]} params.documents - Array of Parse.File documents
   * @returns {Promise<Report>} The created report
   */
  async submitAgencyRegistration({
    user,
    agencyName,
    agencyDescription,
    contactEmail,
    paymentMethod,
    documents = []
  }) {
    // Validate inputs
    if (!user || !agencyName || !agencyDescription || !contactEmail || !paymentMethod) {
      throw new Error('Missing required fields');
    }

    if (!Array.isArray(documents)) {
      throw new Error('Documents must be an array of Parse.File');
    }

    // Create the report
    const report = new Report();

    // Set agency details
    report.setAgencyName(agencyName);
    report.setAgencyDescription(agencyDescription);
    report.setContactEmail(contactEmail);
    report.setPaymentMethod(paymentMethod);
    report.setMessage('I want to validate my agency');

    // Set report metadata
    report.setCategoryQuestionCode(Report.categories.BUSINESS_COOPERATION);
    report.setCategoryQuestion('Commercial cooperation');
    report.setIssueDetailCode(Report.businessApplicationTypes.AGENCY_APPLICATION);
    report.setIssueDetail('Agency application');
    report.setState(Report.states.PENDING);

    // Set user information
    report.setAccuser(user);

    // Set documents
    if (documents.length > 0) {
      report.setImagesList(documents);
    }

    // Save the report
    await report.save();

    // Return the report - AgencyApplication will be created server-side
    return report;
  },
  /**
   * Search for agencies based on a query string
   * @param {string} query - Search term for agency name or code
   * @returns {Promise<Array>} List of matching agencies
   */
  /**
   * Get an agency by its unique code
   * @param {string|number} code - The agency's unique code
   * @returns {Promise<Object|null>} The agency details if found, null otherwise
   * @throws {Error} If code is not a valid number
   */
  async getAgencyByCode(code) {
    try {
      // Convert code to number and validate
      const numericCode = Number(code);
      if (isNaN(numericCode)) {
        throw new Error('Agency code must be a valid number');
      }

      const agency = await Agent.getAgencyByCode(numericCode);
      
      if (!agency) return null;
      
      const author = agency.get(Agent.keys.AUTHOR);

      return {
        id: agency.id,
        name: agency.get(Agent.keys.NAME) ?? author.get(User.keys.FULL_NAME),
        logo: agency.get(Agent.keys.LOGO)?.url() || author.get(User.keys.AVATAR_FILE)?.url(),
        memberCount: agency.get(Agent.keys.HOST_IDS)?.length || 0,
        description: agency.get(Agent.keys.DESCRIPTION) || 'No description available',
        author: author,
        createdAt: agency.get(Agent.keys.CREATED_AT),
        uid: agency.get(Agent.keys.UID) ?? author.get(User.keys.UID),
        rules: agency.get(Agent.keys.RULES) || '',
        commissions: agency.get(Agent.keys.COMMISSIONS) || '',
        support: agency.get(Agent.keys.SUPPORT) || '',
      };
    } catch (error) {
      console.error(`Error getting agency by code '${code}':`, error);
      throw error;
    }
  },

  async searchAgencies(query) {
    try {
      // Create queries for name and code search
      const nameQuery = new Parse.Query('Agents');
      nameQuery.matches('name', new RegExp(query, 'i'));
      
      const codeQuery = new Parse.Query('Agents');
      if (!isNaN(Number(query))) {
        codeQuery.equalTo('uid', Number(query));
      } else {
        // If not a number, make this query never match
        codeQuery.equalTo('uid', -1);
      }
      
      // Combine queries with OR
      const mainQuery = Parse.Query.or(nameQuery, codeQuery);
      mainQuery.include('author');
      mainQuery.limit(20);
      
      // Execute the query
      const agencies = await mainQuery.find();
      
      // Log the number of agencies found
      console.log(`Searched agencies with query '${query}': Found ${agencies.length} agencies`);
      
      // Transform Parse objects to a more usable format
      const formattedAgencies = agencies.map(agency => {
        const logDetails = {
          id: agency.id,
          name: agency.get('name'),
          hostCount: agency.get('hostIds')?.length || 0
        };
        
        console.log('Agency Details:', logDetails);

        const author = agency.get(Agent.keys.AUTHOR);
        
        return {
          id: agency.id,
          name: agency.get(Agent.keys.NAME) ?? author.get(User.keys.FULL_NAME),
          logo: agency.get(Agent.keys.LOGO)?.url() || author.get(User.keys.AVATAR_FILE)?.url(),
          memberCount: agency.get(Agent.keys.HOST_IDS)?.length || 0,
          description: agency.get(Agent.keys.DESCRIPTION) || 'No description available',
          author: author,
          createdAt: agency.get(Agent.keys.CREATED_AT),
          uid: agency.get(Agent.keys.UID) ?? author.get(User.keys.UID),
          rules: agency.get(Agent.keys.RULES) || '',
          commissions: agency.get(Agent.keys.COMMISSIONS) || '',
          support: agency.get(Agent.keys.SUPPORT) || '',
        };
      });
      
      return formattedAgencies;
    } catch (error) {
      console.error(`Agency Search Error for query '${query}':`, error);
      throw error;
    }
  },

  /**
   * Pre-fetch a list of agencies
   * @param {number} limit - Number of agencies to fetch, default 10
   * @returns {Promise<Array>} List of agencies
   */
  async getPreloadedAgencies(limit = 10) {
    try {
      // Create a query for the Agents class
      const agencyQuery = new Parse.Query('Agents');
      
      // Ensure we have a valid class name
      console.log('Query Class Name:', agencyQuery.className);
      
      // Add multiple conditions to find active agencies
      //agencyQuery.exists('name');  // Must have a name
      //agencyQuery.greaterThan('hostIds', []);  // Must have hosts
      
      // Include related user information
      agencyQuery.include('author');
      
      // Limit results
      agencyQuery.limit(limit);
      
      // Sort by number of hosts (most active first), then by creation date
      agencyQuery.descending('hostIds');
      agencyQuery.descending('createdAt');
      
      // Execute the query
      const agencies = await agencyQuery.find();
      
      // Log the number of agencies found
      console.log(`Fetched ${agencies.length} preloaded agencies`);
      
      // Transform Parse objects to a more usable format
      const formattedAgencies = agencies.map(agency => {
        
        const author = agency.get(Agent.keys.AUTHOR);
        return {
          id: agency.id,
          name: agency.get(Agent.keys.NAME) ?? author.get(User.keys.FULL_NAME),
          logo: agency.get(Agent.keys.LOGO)?.url() || author.get(User.keys.AVATAR_FILE)?.url(),
          memberCount: agency.get(Agent.keys.HOST_IDS)?.length || 0,
          description: agency.get(Agent.keys.DESCRIPTION) || 'No description available',
          author: author,
          createdAt: agency.get(Agent.keys.CREATED_AT),
          uid: agency.get(Agent.keys.UID) ?? author.get(User.keys.UID),
          rules: agency.get(Agent.keys.RULES) || '',
          commissions: agency.get(Agent.keys.COMMISSIONS) || '',
          support: agency.get(Agent.keys.SUPPORT) || '',
        };
      });
      
      return formattedAgencies;
    } catch (error) {
      console.error('Error fetching preloaded agencies:', error);
      throw error;
    }
  },

  /**
   * Send a join request to an agency
   * @param {Parse.User} user - Current user sending the request
   * @param {Object} agency - Agency to join
   * @returns {Promise<Parse.Object>} The created join request
   */
  async sendJoinRequest(user, agency) {
    try {
      // Create a new AgencyJoinRequest object
      const AgencyJoinRequest = Parse.Object.extend('AgencyJoinRequest');
      const joinRequest = new AgencyJoinRequest();
      
      // Set request details
      joinRequest.set('user', user);
      joinRequest.set('agency', agency);
      joinRequest.set('status', 'pending');
      
      // Save the join request
      const savedRequest = await joinRequest.save();
      
      return savedRequest;
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      const errorCode = error.code || 'NO_CODE';
      
      console.error(`Join Request Error (${errorCode}):`, errorMessage);
      
      // Throw a more informative error
      throw new Error(`Failed to send join request: ${errorMessage}`);
    }
  },

  /**
   * Get join requests for a specific agency
   * @param {Parse.Object} agency - Agency to fetch join requests for
   * @returns {Promise<Array>} List of join requests
   */
  async getJoinRequests(agency) {
    try {
      const query = new Parse.Query('AgencyJoinRequest');
      query.equalTo('agency', agency);
      query.include('user');
      query.equalTo('status', 'pending');
      
      return await query.find();
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      const errorCode = error.code || 'NO_CODE';
      
      console.error(`Join Requests Fetch Error (${errorCode}):`, errorMessage);
      
      // Throw a more informative error
      throw new Error(`Failed to fetch join requests: ${errorMessage}`);
    }
  }
};

export default agencyService;
